import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import grandLoungeLogo from '../../../assets/img/technology/grand-lounge-white.png';
import {useSelector} from 'react-redux';
import FileSaver from 'file-saver';
import QRCode from 'qrcode';

import {ApiContext} from '../../../api/Api';
import avatar from '../../../assets/img/faces/face-0.jpg';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import EmployeeCorporateContactFormModal from './components/EmployeeCorporateContactFormModal';
import SecurityManager from '../../../utils/SecurityManager';

const CONTACTS_LOADING_ID =
    '@DigitalBusinessCard.employeeCorporateContacts.get';

const DigitalBusinessCard = () => {
    const api = useContext(ApiContext);

    const me = useSelector(state => state.api.me);

    const securityManager = useMemo(
        () => me ? new SecurityManager(me) : null,
        [me]
    );

    const canAdminDigitalBusinessCards =
        !!securityManager
        && securityManager.technology.canAdminDigitalBusinessCards();

    const contacts = useSelector(
        state => state.api.employeeCorporateContacts || []
    );

    const publicCards = useSelector(
        state => state.api.employeePublicCards || []
    );

    const employee = me && me.employee
        ? me.employee
        : null;

    const employeeId = employee
        ? employee.id
        : null;

    const [creatingContact, setCreatingContact] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [showInactiveContacts, setShowInactiveContacts] = useState(false);
    const [changingContactId, setChangingContactId] = useState(null);
    const [publicCard, setPublicCard] = useState(null);
    const [publicCardLoading, setPublicCardLoading] = useState(false);
    const [publicCardLoaded, setPublicCardLoaded] = useState(false);
    const [publicCardSaving, setPublicCardSaving] = useState(false);
    const [publicCardCreationAttempted, setPublicCardCreationAttempted] =
        useState(false);
    const [qrImage, setQrImage] = useState(null);

    useEffect(() => {
        if (!employeeId) {
            return;
        }

        api.employeeCorporateContacts.get({
            pageSize: 100,
            loadingId: CONTACTS_LOADING_ID,
            filters: {
                'employee.id': employeeId,
                'order[sortOrder]': 'ASC',
                'order[id]': 'ASC'
            }
        });
    }, [api, employeeId]);

    useEffect(() => {
        if (!employeeId) {
            setPublicCard(null);
            setPublicCardLoading(false);
            setPublicCardLoaded(false);
            return;
        }

        setPublicCardLoaded(false);
        setPublicCardLoading(true);

        api.employeePublicCards.get({
            pageSize: 1,
            loadingId:
                '@DigitalBusinessCard.employeePublicCards.get',
            filters: {
                'employee.id': employeeId
            },
            customProp: 'employeePublicCards'
        })
            .catch(() => {
                setPublicCard(null);
            })
            .finally(() => {
                setPublicCardLoading(false);
                setPublicCardLoaded(true);
            });
    }, [api, employeeId]);

    useEffect(() => {
        if (!Array.isArray(publicCards)) {
            setPublicCard(null);
            return;
        }

        if (publicCards.length) {
            setPublicCard(publicCards[0]);
            return;
        }

        if (
            !employeeId
            || !publicCardLoaded
            || publicCardLoading
            || publicCardSaving
            || publicCardCreationAttempted
        ) {
            setPublicCard(null);
            return;
        }

        setPublicCardCreationAttempted(true);
        setPublicCardSaving(true);

        api.employeePublicCards.create({
            employeePublicCard: {
                isEnabled: false
            },
            loadingId:
                '@DigitalBusinessCard.employeePublicCards.create',
            customProp: 'employeePublicCards'
        })
            .then(response => {
                setPublicCard(
                    response && response.data
                        ? response.data
                        : null
                );
            })
            .finally(() => {
                setPublicCardSaving(false);
            });
    }, [
        api,
        employeeId,
        publicCards,
        publicCardLoaded,
        publicCardLoading,
        publicCardSaving,
        publicCardCreationAttempted
    ]);

    useEffect(() => {
        if (
            !publicCard
            || !publicCard.publicToken
            || !publicCard.isEnabled
        ) {
            setQrImage(null);
            return;
        }

        const publicCardUrl =
            `${window.location.origin}/card/${
                publicCard.publicToken
            }`;

        QRCode.toDataURL(
            publicCardUrl,
            {
                width: 260,
                margin: 2,
                errorCorrectionLevel: 'M'
            }
        )
            .then(image => {
                setQrImage(image);
            })
            .catch(error => {
                console.error(
                    'No fue posible generar el código QR.',
                    error
                );

                setQrImage(null);
            });
    }, [publicCard]);

    const activeContacts = useMemo(
        () => contacts.filter(contact => contact.isActive),
        [contacts]
    );

    const inactiveContacts = useMemo(
        () => contacts.filter(contact => !contact.isActive),
        [contacts]
    );

    const emailContacts = useMemo(
        () => activeContacts.filter(
            contact => contact.type === 'EMAIL'
        ),
        [activeContacts]
    );

    const phoneContacts = useMemo(
        () => activeContacts.filter(
            contact => contact.type === 'PHONE'
        ),
        [activeContacts]
    );

    const openContactForm = useCallback(
        () => setCreatingContact(true),
        []
    );

    const closeContactForm = useCallback(() => {
        setCreatingContact(false);
        setEditingContact(null);
    }, []);

    const editContact = useCallback(contact => {
        setEditingContact(contact);
    }, []);

    const toggleInactiveContacts = useCallback(() => {
        setShowInactiveContacts(currentValue => !currentValue);
    }, []);

    const reloadContacts = useCallback(() => {
        if (!employeeId) {
            return Promise.resolve();
        }

        return api.employeeCorporateContacts.get({
            pageSize: 100,
            loadingId: CONTACTS_LOADING_ID,
            filters: {
                'employee.id': employeeId,
                'order[sortOrder]': 'ASC',
                'order[id]': 'ASC'
            }
        });
    }, [api, employeeId]);

    const togglePublicCard = useCallback(() => {
        if (!publicCard || publicCardSaving) {
            return;
        }

        setPublicCardSaving(true);

        api.employeePublicCards.edit({
            id: publicCard.id,
            loadingId:
                '@DigitalBusinessCard.employeePublicCards.edit',
            customProp: 'employeePublicCards',
            employeePublicCard: {
                employee: `/api/employees/${employeeId}`,
                isEnabled: !publicCard.isEnabled
            }
        })
            .then(response => {
                setPublicCard(
                    response && response.data
                        ? response.data
                        : {
                            ...publicCard,
                            isEnabled: !publicCard.isEnabled
                        }
                );
            })
            .finally(() => {
                setPublicCardSaving(false);
            });
    }, [
        api,
        employeeId,
        publicCard,
        publicCardSaving
    ]);

    const changeContactActiveStatus = useCallback(
        (contact, nextIsActive) => {
            if (!contact || changingContactId) {
                return;
            }

            const actionText = nextIsActive
                ? 'reactivar'
                : 'desactivar';

            const confirmed = window.confirm(
                `¿Deseas ${actionText} el contacto "${contact.label}"?`
            );

            if (!confirmed) {
                return;
            }

            setChangingContactId(contact.id);

            api.employeeCorporateContacts.edit({
                id: contact.id,
                loadingId:
                    '@DigitalBusinessCard.employeeCorporateContacts.status',
                employeeCorporateContact: {
                    employee: `/api/employees/${employeeId}`,
                    type: contact.type,
                    label: contact.label,
                    value: contact.value,
                    isPrimary: nextIsActive
                        ? contact.isPrimary
                        : false,
                    showOnBusinessCard:
                        contact.showOnBusinessCard,
                    showOnSignature:
                        contact.showOnSignature,
                    sortOrder: contact.sortOrder,
                    isActive: nextIsActive
                }
            })
                .then(() => reloadContacts())
                .finally(() => {
                    setChangingContactId(null);
                });
        },
        [
            api,
            changingContactId,
            employeeId,
            reloadContacts
        ]
    );

    const employeePhoto = employee && employee.photo
        ? api.createFileUrl(employee.photo)
        : avatar;

    const businessCardContacts = useMemo(
        () => activeContacts.filter(
            contact => contact.showOnBusinessCard
        ),
        [activeContacts]
    );

    const businessCardEmail = useMemo(
        () => businessCardContacts.find(
            contact =>
                contact.type === 'EMAIL'
                && contact.isPrimary
        ) || businessCardContacts.find(
            contact => contact.type === 'EMAIL'
        ),
        [businessCardContacts]
    );

    const businessCardPhone = useMemo(
        () => businessCardContacts.find(
            contact =>
                contact.type === 'PHONE'
                && contact.isPrimary
        ) || businessCardContacts.find(
            contact => contact.type === 'PHONE'
        ),
        [businessCardContacts]
    );

    if (!employee) {
        return (
            <div className="container-fluid main-container">
                <div className="alert alert-warning">
                    No fue posible localizar el colaborador asociado a tu
                    usuario de ALMA.
                </div>
            </div>
        );
    }

    return (
        <div
            className="DigitalBusinessCard container-fluid main-container"
            style={{paddingBottom: '45px'}}
        >
            <div
                style={{
                    maxWidth: '1180px',
                    margin: '0 auto'
                }}
            >
                <div style={{marginBottom: '26px'}}>
                    <h2 style={{marginBottom: '7px'}}>
                        Identidad Digital Corporativa
                    </h2>

                    <p
                        className="text-muted"
                        style={{
                            margin: 0,
                            fontSize: '15px'
                        }}
                    >
                        Administra los datos que se mostrarán en tu tarjeta
                        digital corporativa, vCard y código QR.
                    </p>
                </div>

                <div className="row">
                    <div className="col-md-8">
                        <EmployeeSummary
                            employee={employee}
                            photoUrl={employeePhoto}
                        />

                        <div
                            style={{
                                marginTop: '22px',
                                background: '#ffffff',
                                border: '1px solid #e6e9ee',
                                borderRadius: '12px',
                                boxShadow:
                                    '0 4px 16px rgba(32, 45, 64, 0.07)',
                                overflow: 'hidden'
                            }}
                        >
                            <div
                                style={{
                                    padding: '20px 22px',
                                    borderBottom: '1px solid #edf0f4',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <div>
                                    <h4
                                        style={{
                                            margin: 0,
                                            fontWeight: 600
                                        }}
                                    >
                                        Contactos corporativos
                                    </h4>

                                    <p
                                        className="text-muted"
                                        style={{
                                            margin: '5px 0 0'
                                        }}
                                    >
                                        Correos y teléfonos autorizados para
                                        uso corporativo.
                                    </p>
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px'
                                    }}
                                >
                                    <span
                                        className="label label-primary"
                                        style={{
                                            borderRadius: '12px',
                                            padding: '6px 10px'
                                        }}
                                    >
                                        {activeContacts.length} activo
                                        {activeContacts.length === 1 ? '' : 's'}
                                        {' / '}
                                        {contacts.length} registrado
                                        {contacts.length === 1 ? '' : 's'}
                                    </span>

                                    <CustomButton
                                        bsStyle="primary"
                                        bsSize="small"
                                        onClick={openContactForm}
                                    >
                                        <i
                                            className="fa fa-plus"
                                            style={{marginRight: '6px'}}
                                        />
                                        Nuevo contacto
                                    </CustomButton>
                                </div>
                            </div>

                            <div style={{padding: '20px 22px'}}>
                                <ContactSection
                                    title="Correos corporativos"
                                    icon="fa fa-envelope-o"
                                    emptyMessage={
                                        'No hay correos corporativos registrados.'
                                    }
                                    contacts={emailContacts}
                                    onEdit={editContact}
                                    onToggleActive={
                                        changeContactActiveStatus
                                    }
                                    changingContactId={
                                        changingContactId
                                    }
                                />

                                <div
                                    style={{
                                        height: '1px',
                                        background: '#edf0f4',
                                        margin: '24px 0'
                                    }}
                                />

                                <ContactSection
                                    title="Teléfonos empresariales"
                                    icon="fa fa-phone"
                                    emptyMessage={
                                        'No hay teléfonos empresariales activos.'
                                    }
                                    contacts={phoneContacts}
                                    onEdit={editContact}
                                    onToggleActive={
                                        changeContactActiveStatus
                                    }
                                    changingContactId={
                                        changingContactId
                                    }
                                />

                                {inactiveContacts.length ? (
                                    <>
                                        <div
                                            style={{
                                                height: '1px',
                                                background: '#edf0f4',
                                                margin: '24px 0'
                                            }}
                                        />

                                        <InactiveContactsSection
                                            contacts={inactiveContacts}
                                            expanded={showInactiveContacts}
                                            onToggle={
                                                toggleInactiveContacts
                                            }
                                            onEdit={editContact}
                                            onToggleActive={
                                                changeContactActiveStatus
                                            }
                                            changingContactId={
                                                changingContactId
                                            }
                                        />
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    <div className="col-md-4">
                        <BusinessCardPreview
                            employee={employee}
                            photoUrl={employeePhoto}
                            emailContact={businessCardEmail}
                            phoneContact={businessCardPhone}
                        />
                        <PublicCardPanel
                            publicCard={publicCard}
                            publicCardLoading={publicCardLoading}
                            publicCardSaving={publicCardSaving}
                            canAdmin={canAdminDigitalBusinessCards}
                            qrImage={qrImage}
                            publicUrl={
                                publicCard && publicCard.publicToken
                                    ? `${window.location.origin}/card/${publicCard.publicToken}`
                                    : ''
                            }
                            onCopy={() => {
                                if (!publicCard || !publicCard.publicToken) {
                                    return;
                                }

                                navigator.clipboard.writeText(
                                    `${window.location.origin}/card/${publicCard.publicToken}`
                                );
                            }}
                            onOpen={() => {
                                if (!publicCard || !publicCard.publicToken) {
                                    return;
                                }

                                window.open(
                                    `${window.location.origin}/card/${publicCard.publicToken}`,
                                    '_blank'
                                );
                            }}
                            onShare={() => {
                                if (
                                    !publicCard
                                    || !publicCard.publicToken
                                    || !navigator.share
                                ) {
                                    return;
                                }

                                const url =
                                    `${window.location.origin}/card/${publicCard.publicToken}`;

                                navigator.share({
                                    title: 'Tarjeta Digital TGLE',
                                    text: 'Consulta mi tarjeta digital.',
                                    url
                                }).catch(error => {
                                    if (error && error.name !== 'AbortError') {
                                        console.error(
                                            'No fue posible compartir la tarjeta.',
                                            error
                                        );
                                    }
                                });
                            }}
                            onDownloadQr={() => {
                                if (!qrImage) {
                                    return;
                                }

                                FileSaver.saveAs(
                                    qrImage,
                                    'TGLE-DigitalCard-QR.png'
                                );
                            }}
                            onToggle={togglePublicCard}
                        />

                    </div>
                </div>
                {creatingContact || editingContact ? (
                    <EmployeeCorporateContactFormModal
                        employeeId={employeeId}
                        contact={editingContact}
                        onHide={closeContactForm}
                        onSaved={reloadContacts}
                    />
                ) : null}
            </div>
        </div>
    );
};

const EmployeeSummary = ({employee, photoUrl}) => (
    <div
        style={{
            background:
                'linear-gradient(135deg, #182d4f 0%, #244f7b 100%)',
            borderRadius: '14px',
            padding: '26px',
            color: '#ffffff',
            boxShadow: '0 8px 24px rgba(20, 42, 75, 0.18)'
        }}
    >
        <div
            style={{
                display: 'flex',
                alignItems: 'center'
            }}
        >
            <img
                src={photoUrl}
                alt={employee.fullName || 'Colaborador'}
                style={{
                    width: '92px',
                    height: '92px',
                    objectFit: 'cover',
                    borderRadius: '50%',
                    border: '4px solid rgba(255, 255, 255, 0.25)',
                    marginRight: '22px',
                    background: '#ffffff'
                }}
            />

            <div>
                <p
                    style={{
                        margin: '0 0 5px',
                        opacity: 0.75,
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        fontSize: '11px',
                        fontWeight: 600
                    }}
                >
                    Perfil corporativo
                </p>

                <h3
                    style={{
                        margin: '0 0 7px',
                        fontWeight: 600
                    }}
                >
                    {employee.fullName || 'Sin nombre'}
                </h3>

                <p
                    style={{
                        margin: '0 0 3px',
                        fontSize: '15px'
                    }}
                >
                    {employee.position
                        ? employee.position.name
                        : 'Puesto no disponible'}
                </p>

                <p
                    style={{
                        margin: 0,
                        opacity: 0.8
                    }}
                >
                    {employee.area
                        ? employee.area.name
                        : 'Área no disponible'}
                </p>
            </div>
        </div>
    </div>
);

const ContactSection = ({
    title,
    icon,
    contacts,
    emptyMessage,
    onEdit,
    onToggleActive,
    changingContactId
}) => (
    <div>
        <h5
            style={{
                margin: '0 0 14px',
                fontWeight: 600,
                color: '#303b4b'
            }}
        >
            <i
                className={icon}
                style={{
                    marginRight: '9px',
                    color: '#337ab7'
                }}
            />
            {title}
        </h5>

        {!contacts.length ? (
            <div
                style={{
                    padding: '20px',
                    border: '1px dashed #ccd3dc',
                    borderRadius: '9px',
                    textAlign: 'center',
                    color: '#7a8491',
                    background: '#fafbfc'
                }}
            >
                {emptyMessage}
            </div>
        ) : (
            contacts.map(contact => (
                <ContactCard
                    key={contact.id}
                    contact={contact}
                    onEdit={onEdit}
                    onToggleActive={onToggleActive}
                    changingContactId={changingContactId}
                />
            ))
        )}
    </div>
);

const ContactCard = ({
    contact,
    onEdit,
    onToggleActive,
    changingContactId
}) => (
    <div
        style={{
            border: contact.isPrimary
                ? '1px solid #5b9bd5'
                : '1px solid #e3e7ec',
            borderRadius: '10px',
            padding: '15px 17px',
            marginBottom: '12px',
            background: contact.isPrimary
                ? '#f5faff'
                : '#ffffff',
            boxShadow: contact.isPrimary
                ? '0 3px 10px rgba(51, 122, 183, 0.10)'
                : 'none'
        }}
    >
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
            }}
        >
            <div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '5px'
                    }}
                >
                    <strong style={{color: '#2f3946'}}>
                        {contact.label}
                    </strong>

                    {contact.isPrimary ? (
                        <span
                            className="label label-primary"
                            style={{
                                marginLeft: '9px',
                                borderRadius: '10px'
                            }}
                        >
                            Principal
                        </span>
                    ) : null}
                </div>

                <div
                    style={{
                        fontSize: '15px',
                        color: '#4c5968',
                        wordBreak: 'break-word'
                    }}
                >
                    {contact.value}
                </div>
            </div>

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}
            >
                <button
                    type="button"
                    className="btn btn-default btn-xs"
                    title="Editar contacto"
                    disabled={changingContactId === contact.id}
                    onClick={() => onEdit(contact)}
                    style={{
                        borderRadius: '14px',
                        padding: '5px 9px'
                    }}
                >
                    <i className="fa fa-pencil" />
                </button>

                {onToggleActive ? (
                    <button
                        type="button"
                        className={
                            contact.isActive
                                ? 'btn btn-warning btn-xs'
                                : 'btn btn-success btn-xs'
                        }
                        title={
                            contact.isActive
                                ? 'Desactivar contacto'
                                : 'Reactivar contacto'
                        }
                        disabled={changingContactId === contact.id}
                        onClick={() =>
                            onToggleActive(
                                contact,
                                !contact.isActive
                            )
                        }
                        style={{
                            borderRadius: '14px',
                            padding: '5px 9px'
                        }}
                    >
                        <i
                            className={
                                changingContactId === contact.id
                                    ? 'fa fa-spinner fa-spin'
                                    : contact.isActive
                                        ? 'fa fa-pause'
                                        : 'fa fa-check'
                            }
                        />
                    </button>
                ) : null}

                <i
                    className={
                        contact.type === 'EMAIL'
                            ? 'fa fa-envelope'
                            : 'fa fa-phone'
                    }
                    style={{
                        fontSize: '21px',
                        color: '#9aa6b2'
                    }}
                />
            </div>
        </div>

        <div
            style={{
                marginTop: '13px',
                paddingTop: '11px',
                borderTop: '1px solid #edf0f4',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '7px'
            }}
        >
            <VisibilityBadge
                enabled={contact.showOnBusinessCard}
                text="Tarjeta"
            />

            <span
                className={
                    contact.isActive
                        ? 'label label-success'
                        : 'label label-default'
                }
                style={{borderRadius: '10px'}}
            >
                {contact.isActive ? 'Activo' : 'Inactivo'}
            </span>

            <span
                className="label label-default"
                style={{borderRadius: '10px'}}
            >
                Orden {contact.sortOrder}
            </span>
        </div>
    </div>
);

const InactiveContactsSection = ({
    contacts,
    expanded,
    onToggle,
    onEdit,
    onToggleActive,
    changingContactId
}) => (
    <div>
        <button
            type="button"
            className="btn btn-default btn-block"
            onClick={onToggle}
            style={{
                textAlign: 'left',
                borderRadius: '9px',
                padding: '12px 15px',
                background: '#fafbfc',
                borderColor: '#dfe4ea'
            }}
        >
            <i
                className={
                    expanded
                        ? 'fa fa-chevron-up'
                        : 'fa fa-chevron-down'
                }
                style={{
                    marginRight: '9px',
                    color: '#687483'
                }}
            />

            <strong>
                Contactos inactivos ({contacts.length})
            </strong>

            <span
                className="text-muted"
                style={{
                    marginLeft: '8px',
                    fontSize: '12px'
                }}
            >
                Se conservan para consulta y reactivación.
            </span>
        </button>

        {expanded ? (
            <div style={{marginTop: '14px'}}>
                {contacts.map(contact => (
                    <ContactCard
                        key={contact.id}
                        contact={contact}
                        onEdit={onEdit}
                        onToggleActive={onToggleActive}
                        changingContactId={changingContactId}
                    />
                ))}
            </div>
        ) : null}
    </div>
);

const VisibilityBadge = ({enabled, text}) => (
    <span
        className={
            enabled
                ? 'label label-info'
                : 'label label-default'
        }
        style={{borderRadius: '10px'}}
    >
        <i
            className={
                enabled
                    ? 'fa fa-eye'
                    : 'fa fa-eye-slash'
            }
            style={{marginRight: '5px'}}
        />
        {text}
    </span>
);

const BusinessCardPreview = ({
    employee,
    photoUrl,
    emailContact,
    phoneContact
}) => (
    <div
        style={{
            position: 'relative',
            background: '#ffffff',
            border: '1px solid #e6e9ee',
            borderRadius: '14px',
            boxShadow: '0 5px 20px rgba(32, 45, 64, 0.08)',
            overflow: 'hidden'
        }}
    >
        <div
            style={{
                padding: '18px 20px',
                borderBottom: '1px solid #edf0f4'
            }}
        >
            <h4 style={{margin: 0}}>
                Vista previa
            </h4>

            <p
                className="text-muted"
                style={{margin: '5px 0 0'}}
            >
                Así se verá la tarjeta corporativa.
            </p>
        </div>

        <div style={{padding: '22px'}}>
            <div
                style={{
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: '475px',
                    borderRadius: '22px',
                    background:
                        'linear-gradient(155deg, #172d50 0%, #28527e 58%, #3478a5 100%)',
                    boxShadow:
                        '0 14px 34px rgba(20, 42, 75, 0.26)',
                    color: '#ffffff'
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        width: '190px',
                        height: '190px',
                        borderRadius: '50%',
                        top: '-95px',
                        right: '-70px',
                        background: 'rgba(255, 255, 255, 0.08)'
                    }}
                />

                <div
                    style={{
                        position: 'absolute',
                        width: '145px',
                        height: '145px',
                        borderRadius: '50%',
                        bottom: '-75px',
                        left: '-55px',
                        background: 'rgba(255, 255, 255, 0.06)'
                    }}
                />

                <div
                    style={{
                        position: 'relative',
                        padding: '29px 25px 25px',
                        textAlign: 'center'
                    }}
                >


                    <div
                        style={{
                            textAlign: 'left',
                            marginBottom: '22px'
                        }}
                    >
                        <img
                            src={grandLoungeLogo}
                            alt="The Grand Lounge Elite"
                            style={{
                                width: '112px',
                                height: 'auto',
                                display: 'block',
                                opacity: 0.96
                            }}
                        />
                    </div>



                    <img
                        src={photoUrl}
                        alt={employee.fullName || 'Colaborador'}
                        style={{
                            width: '108px',
                            height: '108px',
                            objectFit: 'cover',
                            borderRadius: '50%',
                            border: '5px solid rgba(255, 255, 255, 0.34)',
                            background: '#ffffff',
                            boxShadow:
                                '0 7px 18px rgba(0, 0, 0, 0.22)'
                        }}
                    />

                    <h3
                        style={{
                            margin: '19px 0 7px',
                            fontSize: '22px',
                            lineHeight: 1.2,
                            fontWeight: 700
                        }}
                    >
                        {employee.fullName || 'Sin nombre'}
                    </h3>

                    <p
                        style={{
                            margin: '0 0 4px',
                            fontSize: '14px',
                            fontWeight: 600,
                            opacity: 0.94
                        }}
                    >
                        {employee.position
                            ? employee.position.name
                            : 'Puesto no disponible'}
                    </p>

                    <p
                        style={{
                            margin: 0,
                            fontSize: '12px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.7px',
                            opacity: 0.72
                        }}
                    >
                        {employee.area
                            ? employee.area.name
                            : 'Área no disponible'}
                    </p>

                    <div
                        style={{
                            width: '52px',
                            height: '3px',
                            margin: '21px auto',
                            borderRadius: '3px',
                            background: '#77d2f2'
                        }}
                    />

                    <div
                        style={{
                            textAlign: 'left',
                            borderRadius: '14px',
                            padding: '13px 15px',
                            background: 'rgba(255, 255, 255, 0.11)',
                            border: '1px solid rgba(255, 255, 255, 0.13)'
                        }}
                    >
                        <PreviewContactRow
                            icon="fa fa-envelope-o"
                            value={
                                emailContact
                                    ? emailContact.value
                                    : 'Correo corporativo no definido'
                            }
                            muted={!emailContact}
                        />

                        <div
                            style={{
                                height: '1px',
                                margin: '10px 0',
                                background:
                                    'rgba(255, 255, 255, 0.13)'
                            }}
                        />

                        <PreviewContactRow
                            icon="fa fa-phone"
                            value={
                                phoneContact
                                    ? phoneContact.value
                                    : 'Teléfono empresarial no definido'
                            }
                            muted={!phoneContact}
                        />
                    </div>

                    <div
                        style={{
                            marginTop: '21px',
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '9px'
                        }}
                    >


                       <PreviewAction
                           icon="fa fa-address-book-o"
                           text="Guardar"
                           enabled={
                               !!emailContact || !!phoneContact
                           }
                           onClick={() => downloadVCard({
                               employee,
                               emailContact,
                               phoneContact,
                               photoUrl
                           })}
                       />


                        <PreviewAction
                            icon="fa fa-qrcode"
                            text="QR"
                            enabled={false}
                        />
                    </div>

                    <p
                        style={{
                            margin: '18px 0 0',
                            fontSize: '10px',
                            letterSpacing: '0.8px',
                            opacity: 0.58
                        }}
                    >
                        TARJETA DIGITAL CORPORATIVA
                    </p>
                </div>
            </div>

            <div
                className="alert alert-info"
                style={{
                    margin: '17px 0 0',
                    padding: '10px 12px',
                    fontSize: '12px'
                }}
            >
                <i
                    className="fa fa-refresh"
                    style={{marginRight: '7px'}}
                />
                La vista se actualiza con los contactos activos y visibles
                en tarjeta.
            </div>
        </div>
    </div>
);

const PublicCardPanel = ({
    publicCard,
    publicCardLoading,
    publicCardSaving,
    canAdmin,
    qrImage,
    publicUrl,
    onCopy,
    onOpen,
    onShare,
    onDownloadQr,
    onToggle
}) => (
    <div
        style={{
            marginTop: '20px',
            background: '#fff',
            border: '1px solid #e6e9ee',
            borderRadius: '14px',
            boxShadow: '0 5px 18px rgba(32,45,64,.08)',
            overflow: 'hidden'
        }}
    >
        <div
            style={{
                padding: '18px 20px',
                borderBottom: '1px solid #edf0f4'
            }}
        >
            <h4 style={{margin:0}}>
                Tarjeta pública
            </h4>

            <p
                className="text-muted"
                style={{margin:'6px 0 0'}}
            >
                Controla la publicación de tu tarjeta digital.
            </p>
        </div>

        <div style={{padding:'20px'}}>

            {publicCardLoading && (
                <div className="alert alert-info">
                    Cargando información...
                </div>
            )}

            {!publicCardLoading && publicCard && (

                <>

                    <div
                        style={{
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            marginBottom:'16px'
                        }}
                    >

                        <div>

                            <strong>Estado</strong>

                            <div style={{marginTop:'5px'}}>

                                <span
                                    className={
                                        publicCard.isEnabled
                                            ? 'label label-success'
                                            : 'label label-default'
                                    }
                                >
                                    {publicCard.isEnabled
                                        ? 'Activa'
                                        : 'Pendiente de activación por el área Tecnológica'}
                                </span>

                            </div>

                        </div>

                        {canAdmin ? (
                            <button
                                className={
                                    publicCard.isEnabled
                                        ? 'btn btn-warning'
                                        : 'btn btn-success'
                                }
                                disabled={publicCardSaving}
                                onClick={onToggle}
                            >
                                {
                                    publicCard.isEnabled
                                        ? 'Deshabilitar'
                                        : 'Habilitar'
                                }
                            </button>
                        ) : null}

                    </div>

                    {publicCard.isEnabled ? (
                        <>
                    <div
                        style={{
                            marginBottom:'18px'
                        }}
                    >

                        <label>
                            URL pública
                        </label>

                        <input
                            readOnly
                            className="form-control"
                            value={publicUrl || ''}
                        />

                    </div>

                    <div
                        style={{
                            display:'flex',
                            gap:'10px',
                            flexWrap:'wrap',
                            marginBottom:'20px'
                        }}
                    >

                        <button
                            className="btn btn-default"
                            onClick={onCopy}
                        >
                            <i className="fa fa-copy"/> Copiar
                        </button>

                        <button
                            className="btn btn-primary"
                            onClick={onOpen}
                        >
                            <i className="fa fa-external-link"/>
                            {' '}
                            Abrir
                        </button>

                        {typeof navigator !== 'undefined'
                        && typeof navigator.share === 'function' ? (
                            <button
                                className="btn btn-success"
                                disabled={!publicUrl}
                                onClick={onShare}
                            >
                                <i className="fa fa-share-alt"/>
                                {' '}
                                Compartir
                            </button>
                        ) : null}

                        <button
                            className="btn btn-info"
                            disabled={!qrImage}
                            onClick={onDownloadQr}
                        >
                            <i className="fa fa-download"/>
                            {' '}
                            Descargar QR
                        </button>

                    </div>

                    <div
                        style={{
                            textAlign:'center'
                        }}
                    >

                        {
                            qrImage
                                ? (
                                    <img
                                        src={qrImage}
                                        alt="QR"
                                        style={{
                                            width:220,
                                            height:220,
                                            border:'1px solid #ddd',
                                            padding:'10px',
                                            borderRadius:'10px',
                                            background:'#fff'
                                        }}
                                    />
                                )
                                : (
                                    <div
                                        className="text-muted"
                                    >
                                        QR no disponible.
                                    </div>
                                )
                        }

                    </div>

                        </>
                    ) : (
                        <div
                            className="alert alert-info"
                            style={{
                                marginBottom: 0,
                                fontSize: '13px'
                            }}
                        >
                            <i
                                className="fa fa-info-circle"
                                style={{marginRight: '8px'}}
                            />
                            Tu tarjeta será publicada después de que
                            el área Tecnológica valide la información registrada.
                        </div>
                    )}

                </>

            )}

        </div>

    </div>
);



const blobToBase64 = blob => new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
        const result = String(reader.result || '');
        const base64 = result.includes(',')
            ? result.substring(result.indexOf(',') + 1)
            : result;

        resolve(base64);
    };

    reader.onerror = () => {
        reject(new Error('No fue posible convertir la fotografía.'));
    };

    reader.readAsDataURL(blob);
});

const getVCardPhoto = async photoUrl => {
    if (!photoUrl) {
        return null;
    }

    try {
        const response = await fetch(
            photoUrl,
            {
                credentials: 'include'
            }
        );

        if (!response.ok) {
            return null;
        }

        const blob = await response.blob();

        if (!blob.type.startsWith('image/')) {
            return null;
        }

        const base64 = await blobToBase64(blob);

        return {
            base64,
            type: blob.type === 'image/png'
                ? 'PNG'
                : 'JPEG'
        };
    } catch (error) {
        console.error(
            'No fue posible incluir la fotografía en la vCard.',
            error
        );

        return null;
    }
};

const foldVCardLine = line => {
    const maxLength = 75;

    if (line.length <= maxLength) {
        return line;
    }

    const parts = [];

    for (
        let index = 0;
        index < line.length;
        index += maxLength
    ) {
        parts.push(line.substring(index, index + maxLength));
    }

    return parts.join('\r\n ');
};


const escapeVCardValue = value => String(value || '')
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');

const createVCardFilename = employee => {
    const cleanName = String(
        employee && employee.fullName
            ? employee.fullName
            : 'contacto-corporativo'
    )
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

    return `${cleanName || 'contacto-corporativo'}.vcf`;
};

const downloadVCard = async ({
    employee,
    emailContact,
    phoneContact,
    photoUrl
}) => {

    const lines = [
        'BEGIN:VCARD',
        'VERSION:3.0',
        `FN:${escapeVCardValue(employee.fullName)}`,
        'ORG:The Grand Lounge Elite'
    ];

    const photo = await getVCardPhoto(photoUrl);

    if (photo && photo.base64) {
        lines.push(
            `PHOTO;ENCODING=b;TYPE=${photo.type}:${photo.base64}`
        );
    }

    if (employee.position && employee.position.name) {
        lines.push(
            `TITLE:${escapeVCardValue(employee.position.name)}`
        );
    }

    if (employee.area && employee.area.name) {
        lines.push(
            `X-DEPARTMENT:${escapeVCardValue(employee.area.name)}`
        );
    }

    if (emailContact && emailContact.value) {
        lines.push(
            `EMAIL;TYPE=INTERNET,WORK:${escapeVCardValue(
                emailContact.value
            )}`
        );
    }

    if (phoneContact && phoneContact.value) {
        lines.push(
            `TEL;TYPE=CELL,WORK:${escapeVCardValue(
                phoneContact.value
            )}`
        );
    }

    lines.push('END:VCARD');

    const vCardContent = `${
        lines
            .map(line =>
                line.startsWith('PHOTO;')
                    ? line
                    : foldVCardLine(line)
            )
            .join('\r\n')
    }\r\n`;

    const blob = new Blob(
        [vCardContent],
        {
            type: 'text/vcard;charset=utf-8'
        }
    );

    FileSaver.saveAs(
        blob,
        createVCardFilename(employee)
    );
};


const PreviewContactRow = ({
    icon,
    value,
    muted
}) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            opacity: muted ? 0.58 : 1
        }}
    >
        <i
            className={icon}
            style={{
                width: '24px',
                marginRight: '8px',
                textAlign: 'center',
                color: '#9fe0f7'
            }}
        />

        <span
            style={{
                overflow: 'hidden',
                fontSize: '12px',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
            }}
            title={value}
        >
            {value}
        </span>
    </div>
);

const PreviewAction = ({
    icon,
    text,
    enabled,
    onClick
}) => (

    <button
        type="button"
        className="btn btn-default btn-sm"
        disabled={!enabled}
        onClick={enabled ? onClick : undefined}
        style={{
            minWidth: '88px',
            borderRadius: '18px',
            color: enabled ? '#234a73' : '#8a96a3',
            background: enabled
                ? '#ffffff'
                : 'rgba(255, 255, 255, 0.65)',
            border: 0,
            fontWeight: 600
        }}
    >
        <i
            className={icon}
            style={{marginRight: '6px'}}
        />
        {text}
    </button>
);

export default DigitalBusinessCard;
